import { useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@core/ui/cn';
import { ReleaseMarks, taskTipLines } from '../shared/TaskBar';
import { Tooltip } from '@core/ui/Tooltip';
import { dateRange, type ISODate } from '@core/dates';
import type { Attention, Task } from '../shared/data';
import { pressDrag } from '../shared/interaction';
import { STATUS_LOOK } from '../../launch/planner';
import { KEY_RELEASE_CLASS, releaseMenu, type CardActions, type Person, type ProjectInfo } from './ReleaseCard';
import { Menu } from '@core/ui/Menu';
import { SquarePen } from 'lucide-react';
import { useCanWrite } from '@core/auth/access';
import { isCurrentWeek, type LaunchRelease, type Release, type Week } from './planner';

// Чипы Timeline над и под осью недель: тянутся между неделями той же механикой, что карточки Weeks.

type CombProps = { weeks: Week[]; weekWidth: number; byWeek: Map<ISODate, Release[]>; today: ISODate; projectOf: (t: Task) => ProjectInfo; peopleOf: (t: Task) => Person[]; attention: Map<string, Attention>; actions: CardActions; onOpen: (r: Release) => void; onDrop: (r: Release, monday: ISODate) => void };

export function Comb({ weeks, weekWidth, byWeek, today, projectOf, peopleOf, attention, actions, onOpen, onDrop }: CombProps) {
  const [drag, setDrag] = useState<{ id: string; from: number; dx: number } | null>(null);
  const target = drag ? Math.max(0, Math.min(weeks.length - 1, drag.from + Math.round(drag.dx / weekWidth))) : -1;
  // та же механика, что у карточек Weeks и баров сетки: до 4px — клик (двойной клик не путается с протяжкой)
  const startDrag = (r: Release, from: number, e: ReactPointerEvent<HTMLElement>) => {
    let dx = 0;
    pressDrag(e, r.id, {
      onMove: (x) => { dx = x; setDrag({ id: r.id, from, dx: x }); },
      onDrop: () => { setDrag(null); const to = Math.max(0, Math.min(weeks.length - 1, from + Math.round(dx / weekWidth))); if (to !== from) onDrop(r, weeks[to]!.monday); },
    });
  };
  const column = (side: 'above' | 'below') => (
    <div className={cn('flex items-stretch', side === 'above' ? 'items-end' : 'items-start')}>
      {weeks.map((w, i) => {
        const mine = (i % 2 === 0) === (side === 'above');
        const list = mine ? byWeek.get(w.monday) ?? [] : [];
        return (
          <div key={w.monday} className={cn('flex flex-col gap-1 px-1 py-1 shrink-0', side === 'above' ? 'justify-end' : 'justify-start')} style={{ width: weekWidth }}>
            {list.map((r) => (r.kind === 'launch'
              ? <LaunchChip key={r.id} release={r} onOpen={() => onOpen(r)} />
              : <Chip key={r.id} task={r.task} project={projectOf(r.task)} names={peopleOf(r.task).map((p) => p.name)} attention={attention.get(r.id)} actions={actions} dragging={drag?.id === r.id} dx={drag?.id === r.id ? drag.dx : 0} onPointerDown={(e) => startDrag(r, i, e)} onOpen={() => onOpen(r)} />
            ))}
          </div>
        );
      })}
    </div>
  );
  return (
    <div className="mt-2 relative" data-comb>
      {/* цель протяжки — вся колонка недели, сверху донизу */}
      {drag && target >= 0 && <div data-drop-target={weeks[target]!.monday} className="absolute inset-y-0 rounded bg-blue-100/70 ring-2 ring-inset ring-accent pointer-events-none" style={{ left: target * weekWidth, width: weekWidth }} />}
      {column('above')}
      {/* ось: точки на границах недель, подписи недель по очереди снизу и сверху */}
      <div className="relative" style={{ height: 44 }}>
        <div className="absolute left-0 right-0 top-1/2 border-t-2 border-violet-500" />
        {weeks.map((w, i) => {
          const current = isCurrentWeek(w, today);
          return (
            <div key={w.monday} className="absolute top-0 bottom-0" style={{ left: i * weekWidth, width: weekWidth }}>
              <span className={cn('absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 bg-white', current ? 'border-red-500' : 'border-violet-500')} />
              <span className={cn('absolute left-0 right-0 text-center text-xs whitespace-nowrap', i % 2 === 0 ? 'bottom-0' : 'top-0', current ? 'text-red-600 font-bold' : 'text-gray-600')}>{w.label}</span>
            </div>
          );
        })}
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-violet-500 bg-white" style={{ left: weeks.length * weekWidth }} />
      </div>
      {column('below')}
    </div>
  );
}

function Chip({ task, project, names, attention, actions, dragging, dx, onPointerDown, onOpen }: { task: Task; project: ProjectInfo; names: string[]; attention: Attention | undefined; actions: CardActions; dragging: boolean; dx: number; onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void; onOpen: () => void }) {
  const canWrite = useCanWrite();
  const chip = (
    <div
      role="button" tabIndex={0} data-chip={task.id} data-key-release={task.key_release || undefined} data-attention={attention ? attention.name : undefined}
      className={cn('rounded-md border shadow-sm px-1.5 py-1 text-xs leading-tight cursor-grab select-none focus:outline-none focus-visible:ring-2 ring-accent/70', KEY_RELEASE_CLASS(task.key_release), dragging && 'opacity-70 shadow-lg z-10 relative cursor-grabbing')}
      style={{ borderLeft: `3px solid ${project.color}`, transform: dragging ? `translateX(${dx}px)` : undefined, outline: attention ? `2px solid ${attention.color}` : undefined, outlineOffset: 1 }}
      onPointerDown={onPointerDown} onDoubleClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    >
      <div className="font-semibold text-gray-900 break-words"><ReleaseMarks task={task} size="w-3.5 h-3.5 text-[10px]" />{task.title}</div>
      <div className="uppercase tracking-wide text-[10px] font-semibold truncate" style={{ color: project.color }}>{project.name}</div>
    </div>
  );
  return <Menu items={releaseMenu(task, actions, canWrite)}><Tooltip disabled={dragging} title={task.title} lines={[project.name, ...taskTipLines(task, names)]} hint="Drag to another week · double-click to open">{chip}</Tooltip></Menu>;
}

/** Чип релиза лонча: подпись Launch цветом статуса release, имя лонча в тултипе; не тянется — дата меняется на Launch Board. */
function LaunchChip({ release, onOpen }: { release: LaunchRelease; onOpen: () => void }) {
  const look = STATUS_LOOK.release;
  return (
    <Menu items={[{ label: 'Open the launch task', onSelect: onOpen, icon: SquarePen }]}>
    <Tooltip title={release.title} lines={[release.launch.name, dateRange(release.task.start_date, release.task.end_date)]} hint="Double-click to open the launch task">
    <div
      role="button" tabIndex={0} data-chip={release.id} data-launch-release
      className="rounded-md border shadow-sm px-1.5 py-1 text-xs leading-tight select-none bg-white border-gray-200 focus:outline-none focus-visible:ring-2 ring-accent/70"
      style={{ borderLeft: `3px solid ${look.border}` }}
      onDoubleClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    >
      <div className="font-semibold text-gray-900 break-words">{release.title}</div>
      <div className="uppercase tracking-wide text-[10px] font-semibold truncate" style={{ color: look.text }}>Launch</div>
    </div>
    </Tooltip>
    </Menu>
  );
}
